import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/_shared/components/ui/button';

import { type HostedUiConfig, startHostedUiSignIn } from '../../lib/hosted-ui';
import { cognitoStartUrl } from '../../lib/lookout-session';
import { signInErrorMessage } from '../../lib/sign-in-errors';

/**
 * Starts a Google sign-in. With the shared session (ACT2-879), the page runs the Hosted UI itself. Without it, the
 * button is a link to Lookout's server flow.
 */
export function FederatedStartButton({
  hostedUi,
  options,
  variant = 'secondary',
  onStart,
  children,
  ref,
}: {
  hostedUi: HostedUiConfig | null;
  options: Parameters<typeof cognitoStartUrl>[1];
  variant?: 'primary' | 'secondary';
  /** Called on the click, so the page can hide the error of an earlier attempt. */
  onStart?: () => void;
  children: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
}): React.ReactElement {
  const [leaving, setLeaving] = useState(false);
  const target = { provider: 'google' } as const;
  if (!hostedUi) {
    return (
      <Button ref={ref} variant={variant} size="lg" className="w-full gap-2" asChild>
        <a href={cognitoStartUrl(target, options)} onClick={onStart}>
          {children}
        </a>
      </Button>
    );
  }
  return (
    <Button
      ref={ref}
      type="button"
      variant={variant}
      size="lg"
      className="w-full gap-2"
      loading={leaving}
      onClick={() => {
        onStart?.();
        setLeaving(true);
        startHostedUiSignIn(hostedUi, target, options).catch(() => {
          setLeaving(false);
          toast.error(signInErrorMessage('federated_signin_failed'));
        });
      }}
    >
      {children}
    </Button>
  );
}
